import { useState, useEffect } from "react";
import { useMediaQuery } from "react-responsive";
import { useDispatch } from "react-redux";

import {
    PredictionBarChart,
    RatePrediction,
    PredictionLineChart,
    PredictionLoadRatesMap,
    PredictionLoadRatesBestOutbounds,
    LockedWrapper,
    LockedTooltip,
} from "@/components/dataPages";
import { setInputSearch, clearInputs } from "@/state/searchInputSlice";
import { setPickupLocation, setDeliveryLocation, clearLocations } from "@/state/searchSlice";
import { setMarketToMarketRoute } from "@/state/loadRatesSlice";
import { setCurrentSearch, clearCurrentSearch } from "@/state/currentSearchSlice";
import { lockedSearchInputLoadRates, lockedPickupLocation, lockedDeliveryLocation, lockedMarketToMarketRoute } from "@/utils/lockedData";

export const LockedLoadRates = () => {
    const [filteredMarkets, setFilteredMarkets] = useState(null);
    const [activeTooltip, setActiveTooltip] = useState(null);
    const dispatch = useDispatch();
    const isDesktop = useMediaQuery({
        query: "(min-width: 1024px)",
    });

    useEffect(() => {
        dispatch(
            setInputSearch({
                pickup: lockedSearchInputLoadRates.pickup,
                pickupQuery: lockedSearchInputLoadRates.pickupQuery,
                delivery: lockedSearchInputLoadRates.delivery,
                deliveryQuery: lockedSearchInputLoadRates.deliveryQuery,
                trailerType: lockedSearchInputLoadRates.trailerType,
                pickupDate: lockedSearchInputLoadRates.pickupDate,
                pickupRadius: lockedSearchInputLoadRates.pickupRadius,
                deliveryRadius: lockedSearchInputLoadRates.deliveryRadius,
            }),
        );
        dispatch(setPickupLocation(lockedPickupLocation));
        dispatch(setDeliveryLocation(lockedDeliveryLocation));
        dispatch(setMarketToMarketRoute(lockedMarketToMarketRoute));
        dispatch(
            setCurrentSearch({
                trailerType: lockedSearchInputLoadRates.trailerType,
                pickup: lockedSearchInputLoadRates.pickup,
                delivery: lockedSearchInputLoadRates.delivery,
                pickupDate: lockedSearchInputLoadRates.pickupDate,
            }),
        );

        return () => {
            dispatch(clearInputs());
            dispatch(clearLocations());
            dispatch(clearCurrentSearch());
        };
    }, [dispatch]);

    const handleHover = (component) => {
        setActiveTooltip(component);
    };

    const handleLeave = () => {
        setActiveTooltip(null);
    };

    return (
        <div className="relative">
            <div className="relative flex flex-col lg:flex-row bg-cm-p-surface rounded-lg w-full pt-4 px-6 pb-6 drop-shadow-[0_4px_4px_rgba(0,0,0,0.05)]">
                <div className="flex flex-col sm:flex-row justify-between gap-5 sm:gap-2">
                    <div className="w-[230px]">
                        <LockedWrapper
                            isActive={activeTooltip === 'ratePrediction'}
                            onHover={() => handleHover('ratePrediction')}
                            onLeave={handleLeave}
                        >
                            <RatePrediction />
                        </LockedWrapper>
                    </div>
                    {!isDesktop && (
                        <div className="w-full border-t border-cm-p-outline-variation py-4 sm:w-[282px] sm:border-0 sm:py-0">
                            <LockedWrapper
                                isActive={activeTooltip === 'barChart'}
                                onHover={() => handleHover('barChart')}
                                onLeave={handleLeave}
                            >
                                <PredictionBarChart
                                    withDelivery
                                    title="Load volume"
                                    tooltipText="Load volume by day gives insights of frequency of loads available shown by day of the pickup, in percentages."
                                />
                            </LockedWrapper>
                        </div>
                    )}
                </div>

                <div className="lg:mx-4 xl:ml-16 xl:mr-14 w-full lg:w-[calc(100% - 230px - 282px - 120px)] overflow-hidden">
                    <LockedWrapper
                        isActive={activeTooltip === 'lineChart'}
                        onHover={() => handleHover('lineChart')}
                        onLeave={handleLeave}
                    >
                        <PredictionLineChart />
                    </LockedWrapper>
                </div>
                {isDesktop && (
                    <div className="w-[282px] min-w-[282px]">
                        <LockedWrapper
                            isActive={activeTooltip === 'barChart'}
                            onHover={() => handleHover('barChart')}
                            onLeave={handleLeave}
                        >
                            <PredictionBarChart
                                withDelivery
                                title="Load volume"
                                tooltipText="Load volume by day gives insights of frequency of loads available shown by day of the pickup, in percentages."
                            />
                        </LockedWrapper>
                    </div>
                )}
                <LockedTooltip
                    content={<>See <span className="font-bold">estimated flat rates and RPM</span> values for your lane - plan smarter with full trip cost visibility and precise profit forecasting</>}
                    tooltipId="ratePrediction"
                    activeTooltip={activeTooltip}
                    width="362px"
                    positionConfig={{
                        default: { mediumScreen: { top: '114px', left: '-10px' }, desktop: { top: '114px', left: '-30px' } },
                        active: { mediumScreen: { top: '114px', left: '248px' }, desktop: { top: '114px', left: '248px' } },
                        lineChart: { mediumScreen: { top: '514px', left: '-20px' }, desktop: { top: '114px', left: '-90px' } },
                        barChart: { mediumScreen: { top: '214px', left: '12px' }, desktop: { top: '114px', left: '-44px' } },
                        map: { mediumScreen: { top: '100px', left: '10px' }, desktop: { top: '100px', left: '-34px' } }
                    }}
                />
                <LockedTooltip
                    content={<>Track past <span className="font-bold">market trends</span> to anticipate rate changes and make confident decisions</>}
                    tooltipId="lineChart"
                    activeTooltip={activeTooltip}
                    width="357px"
                    positionConfig={{
                        default: { mediumScreen: { top: '224px', left: '340px' }, desktop: { top: '194px', left: '542px' } },
                        active: { mediumScreen: { top: '377px', left: '340px' }, desktop: { top: '377px', left: '542px' } },
                        ratePrediction: { mediumScreen: { top: '194px', left: '618px' }, desktop: { top: '194px', left: '658px' } },
                        barChart: { mediumScreen: { top: '374px', left: '211px' }, desktop: { top: '194px', left: '511px' } },
                        map: { mediumScreen: { top: '179px', left: '542px' }, desktop: { top: '179px', left: '542px' } }
                    }}
                />
                <LockedTooltip
                    content={<>Track daily <span className="font-bold">load trends</span> and market activity to dispatch at the most profitable times</>}
                    tooltipId="barChart"
                    activeTooltip={activeTooltip}
                    width="362px"
                    positionConfig={{
                        default: { mediumScreen: { top: '68px', right: '-9px' }, desktop: { top: '68px', right: '-9px' } },
                        active: { mediumScreen: { top: '68px', right: '340px' }, desktop: { top: '68px', right: '340px' } },
                        ratePrediction: { mediumScreen: { top: '68px', right: '5px' }, desktop: { top: '68px', right: '-35px' } },
                        lineChart: { mediumScreen: { top: '68px', right: '-53px' }, desktop: { top: '68px', right: '-53px' } },
                        map: { mediumScreen: { top: '59px', right: '-11px' }, desktop: { top: '59px', right: '-11px' } }
                    }}
                />
            </div>
            
            <div className="relative mt-8">
                <LockedWrapper
                    isActive={activeTooltip === 'map'}
                    onHover={() => handleHover('map')}
                    onLeave={handleLeave}
                >
                    <div className="overflow-hidden relative h-[1048px] md:h-[762px] lg:h-[397px] w-full flex rounded-lg flex-col">
                        <PredictionLoadRatesMap filteredMarkets={filteredMarkets} />
                        <PredictionLoadRatesBestOutbounds setMarketsToPin={setFilteredMarkets} isLocked />
                    </div>
                </LockedWrapper>
                <LockedTooltip
                    content={<>Find your <span className="font-bold">next best-paying trip</span> instantly with data-driven outbounds recommendations based on real-life profitability</>}
                    tooltipId="map"
                    activeTooltip={activeTooltip}
                    width="376px"
                    positionConfig={{
                        default: { mediumScreen: { top: '48px', left: '331px' }, desktop: { top: '36px', left: '531px' } },
                        active: { mediumScreen: { top: '-70px', left: '331px' }, desktop: { top: '-120px', left: '531px' } },
                        ratePrediction: { mediumScreen: { top: '58px', left: '581px' }, desktop: { top: '58px', left: '581px' } },
                        lineChart: { mediumScreen: { top: '89px', left: '531px' }, desktop: { top: '89px', left: '531px' } },
                        barChart: { mediumScreen: { top: '50px', left: '511px' }, desktop: { top: '50px', left: '511px' } }
                    }}
                />
            </div>
        </div>
    );
};
